import { useCallback, useMemo } from 'react';
import { useUserPreferencesQuery, useUpdateUserPreferences } from '@/hooks/useUserPreferences';
import { USER_PREF_KEYS } from '@/services/userPreferencesService';
import type { ModuleNavigationConfig, NavigationMenuItem } from '@/config/navigation.generated';

/** The most favorites a user can keep; the server refuses more (MenuFavoritesSetting.MaxCount). */
export const MAX_FAVORITES = 50;

export interface FavoriteMenuItem {
  item: NavigationMenuItem;
  module: ModuleNavigationConfig;
}

/** The stored list, or none when it is missing or not a list of ids. */
export function parseFavorites(raw: string | undefined): string[] {
  try {
    const parsed: unknown = JSON.parse(raw ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * The favorites the menu shows, in the user's order. Only items the user can open are among the
 * modules; a favorite without one (permission lost, item removed) stays stored and is not shown.
 */
export function resolveFavoriteItems(
  favorites: string[],
  modules: ModuleNavigationConfig[],
): FavoriteMenuItem[] {
  const byId = new Map<string, FavoriteMenuItem>();
  for (const module of modules) {
    for (const category of module.categories) {
      for (const item of category.items) byId.set(item.id, { item, module });
    }
  }
  return favorites.flatMap((id) => byId.get(id) ?? []);
}

/** The full list with `fromId` moved to where `toId` stands. */
export function moveFavoriteId(favorites: string[], fromId: string, toId: string): string[] {
  const from = favorites.indexOf(fromId);
  const to = favorites.indexOf(toId);
  if (from < 0 || to < 0 || from === to) return favorites;
  const next = [...favorites];
  next.splice(from, 1);
  next.splice(to, 0, fromId);
  return next;
}

/**
 * The user's starred menu items, in the order they arranged them. Stored per user as the
 * Nexo.User.MenuFavorites preference, so they follow the user to every browser.
 */
export function useFavorites() {
  const { data } = useUserPreferencesQuery();
  const { mutate } = useUpdateUserPreferences();

  const favorites = useMemo(() => parseFavorites(data?.[USER_PREF_KEYS.menuFavorites]), [data]);

  const save = useCallback(
    (next: string[]) => mutate({ [USER_PREF_KEYS.menuFavorites]: JSON.stringify(next) }),
    [mutate],
  );

  const isFavorite = useCallback((itemId: string) => favorites.includes(itemId), [favorites]);

  /** Removes a favorite, or adds it last. */
  const toggleFavorite = useCallback(
    (itemId: string) =>
      save(favorites.includes(itemId) ? favorites.filter((id) => id !== itemId) : [...favorites, itemId]),
    [favorites, save],
  );

  /** Moves a favorite to where another one stands (drag and drop). */
  const moveFavorite = useCallback(
    (fromId: string, toId: string) => {
      const next = moveFavoriteId(favorites, fromId, toId);
      if (next !== favorites) save(next);
    },
    [favorites, save],
  );

  return {
    favorites,
    isFavorite,
    toggleFavorite,
    moveFavorite,
    atCap: favorites.length >= MAX_FAVORITES,
  };
}

export type UseFavoritesReturn = ReturnType<typeof useFavorites>;
